import { mediaQueries } from '@tallink/components-lib'
import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useMedia, useUpdateEffect } from 'react-use'

import { selectPaymentMethod } from '../../../../actions/actions'
import {
  getAvailablePaymentMethods,
  getPayment,
  hasCompanyAgentLogin,
  isFreeReservation
} from '../../../../selectors/selectors'
import InvoicePaymentView from '../InvoicePayment/InvoicePaymentView'
import KlarnaPaymentView from '../KlarnaPaymentView/KlarnaPaymentView'
import PayButton from '../PayButton/PayButton'
import PaymentDisclaimer from '../PaymentDisclaimer/PaymentDisclaimer'
import PaymentOptions from '../PaymentOptions/PaymentOptions'
import s from './PaymentTabs.module.scss'

const paymentMethodViews = {
  klarna: <KlarnaPaymentView />,
  invoice: <InvoicePaymentView />
}

export default function PaymentTabs() {
  const ref = useRef()
  const { method: selectedMethod } = useSelector(getPayment)
  const freeReservation = useSelector(isFreeReservation)

  useUpdateEffect(() => {
    function scrollToSelfIfNeeded() {
      if (ref && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
      }
    }

    scrollToSelfIfNeeded()
  }, [selectedMethod])

  return (
    <section className={s['payment-tabs']} ref={ref}>
      {freeReservation ? <FreeReservationContent /> : <NotFreeReservationContent />}
    </section>
  )
}

function FreeReservationContent() {
  const { t } = useTranslation()
  const smUp = useMedia(mediaQueries.smUp, true)

  return (
    <>
      {smUp && <header>{t('payment.title')}</header>}
      <div className={s['selected-tab-view']}>
        <PaymentDisclaimer />
      </div>
      <PayButton />
    </>
  )
}

function NotFreeReservationContent() {
  const { t } = useTranslation()
  const { method: selectedMethod } = useSelector(getPayment)
  const companyAgentLogin = useSelector(hasCompanyAgentLogin)

  const invoicePaymentTypeSelected = selectedMethod === 'invoice'
  const smUp = useMedia(mediaQueries.smUp, true)

  return (
    <>
      {companyAgentLogin && <PaymentOptions />}
      {smUp && <header>{t('payment.title')}</header>}
      {!invoicePaymentTypeSelected && <PaymentMethodSelector />}
      <div className={s['selected-tab-view']}>
        <div className={s.tab}>{paymentMethodViews[selectedMethod]}</div>
        <PaymentDisclaimer />
      </div>
      <PayButton />
    </>
  )
}

function PaymentMethodSelector() {
  const availablePaymentMethods = useSelector(getAvailablePaymentMethods)

  return (
    availablePaymentMethods.length > 1 && (
      <nav>
        <form className={s['radio-buttons-container']}>
          {availablePaymentMethods.map(method => (
            <PaymentMethod key={method.code} method={method} />
          ))}
        </form>
      </nav>
    )
  )
}

function PaymentMethod({ method }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { method: selectedMethod, paymentConfirmed } = useSelector(getPayment)

  const handleRadioChange = () => {
    dispatch(selectPaymentMethod(method.code))
  }

  const { title, description } = getPaymentMethodInfo(method, t)

  return (
    <div className={s['payment-option']}>
      <input
        checked={selectedMethod === method.code}
        disabled={paymentConfirmed}
        id={method.code}
        name="paymentMethod"
        onChange={handleRadioChange}
        type="radio"
        value={method.code}
      />
      <label htmlFor={method.code}>
        <span className={s['custom-radio']} />
        <div className={s['method-details']}>
          <span className={s.name}>{title}</span>
          <span className={s.description}>{description}</span>
        </div>
      </label>
    </div>
  )
}

PaymentMethod.propTypes = {
  method: PropTypes.object.isRequired
}

function getPaymentMethodInfo(method, t) {
  const METHOD_TO_TITLE = {
    SWEDBANK: 'payment.onlineBank.title',
    ADYEN: method?.methods?.includes('BANK') ? 'payment.onlineBankAndCards.title' : 'payment.cards.title',
    klarna: 'payment.klarna.title'
  }

  const METHOD_TO_DESCRIPTION = {
    SWEDBANK: 'payment.onlineBanks.description',
    klarna: 'payment.klarna.description'
  }

  const titleKey = METHOD_TO_TITLE[method.code] || `payment.${method.code}.title`
  const descriptionKey = METHOD_TO_DESCRIPTION[method.code] || `payment.${method.code}.description`

  return {
    title: t(titleKey),
    description:
      method.code === 'ADYEN'
        ? method.methods.map(methodCode => t(`payment.${methodCode}.description`)).join(', ')
        : t(descriptionKey)
  }
}
